import * as React from 'react';
import { DatePicker, DatePickerProps } from '@material-ui/pickers';
import { InputAdornment, makeStyles, createStyles } from '@material-ui/core';
import { Event as CalendarIcon } from '@material-ui/icons';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import cn from 'classnames';
import TextField from '../TextField/TextField';
import { TranquiTheme } from '../../containers/App/theme';

type DayPickerProps = Omit<DatePickerProps, 'onChange' | 'value'> & {
  value: string;
  onChange: (day: string) => void;
  defaultDay?: string;
};
const baseMonth = moment({ month: 6, year: 2019 });
const baseMonthEnd = baseMonth.clone().endOf('month');
const baseMonthStart = baseMonth.clone().startOf('month');

const DayPicker: React.FC<DayPickerProps> = ({ onChange, defaultDay, className, ...props }) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles({ label: props.label });

  const raw = parseInt(props.value, 10);
  const value = raw ? baseMonth.clone().day(raw) : null;
  const handleChange = (date: MaterialUiPickersDate) => onChange(date!.format('D'));

  let format = '[Día] D';
  if (value?.isSame(baseMonthEnd, 'day')) format = '[Último día]';
  if (value?.isSame(baseMonthStart, 'day')) format = '[Primer día]';
  format += ' [de cada mes]';

  return (
    <DatePicker
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      value={value}
      onChange={handleChange}
      disableToolbar
      format={props.format || format}
      TextFieldComponent={(innerProps) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...innerProps}
          variant="outlined"
          InputProps={{
            endAdornment: <InputAdornment position="end" className={classes.icon}><CalendarIcon /></InputAdornment>,
          }}
        />
      )}
      okLabel={false}
      cancelLabel="Cancelar"
      DialogProps={{ className: classes.picker }}
      className={cn(classes.root, className)}
      minDate={baseMonthStart}
      maxDate={baseMonthEnd}
      autoOk
    />
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    '& .MuiInputLabel-outlined': {
      ...theme.typography.body2,
      color: theme.palette.text4.main,
      lineHeight: 'initial',
    },
    '&.Mui-focused': {
      color: theme.palette.black.main,
    },
    '& .MuiOutlinedInput-root': {
      border: 'none',
      borderBottom: 'solid',
      borderColor: `${theme.palette.text5.main}!important`,
      borderWidth: '1px !important',
      borderRadius: '0 !important',
      ...theme.typography.body2,
      color: theme.palette.text2.main,
      fontWeight: 600,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  picker: {
    '& .MuiPickersCalendarHeader-switchHeader, & .MuiPickersCalendarHeader-daysHeader': {
      display: 'none',
    },
    '& .MuiPickersBasePicker-pickerView': {
      minHeight: 0,
    },
    '& .MuiPickersCalendar-transitionContainer': {
      marginTop: 20,
      '&::before': {
        content: ({ label }: any) => `"${label || ''}"`,
        textAlign: 'center',
        display: 'block',
        lineHeight: 1.3,
        padding: theme.spacing(0, 3),
        marginTop: -6,
        ...theme.typography.body1,
        color: theme.palette.text1.main,
      },
      '& div': {
        position: 'relative',
        '& .MuiPickersCalendar-week': {
          '&. MuiPickersDay-daySelected': {
            backgroundColor: 'transparent',
            borderRadius: `solid 1px ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    '& .MuiPickersDay-daySelected': {
      backgroundColor: 'transparent',
      border: `solid 1px ${theme.palette.secondary.main}`,
    },
  },
  icon: {
    color: theme.palette.text5.main,
  },
}));

export default DayPicker;
